import GeoBase, {IGeoParams} from './geo_base';
import DOM from '../../utils/dom';
import Event from '../../utils/event';
import {GEO_LOCATION} from './geo_consts';
import {GeoPluginResults} from "./geo_plugin_result";
import GeoDataFormatter from "./geo_data_formatter";
import {SYSTEM_EVENTS} from "../../events/events";

export interface IGeoPCParams extends IGeoParams{
    GCinstallerID:string;
    GCenvironment:string;
    downloadVersion:string;
    installParams:string;
    installPath:string;
    downloadGC:any;
    loadScriptJS:any;
    geoLib:string;
}

export default class GeoPc extends GeoBase {

    static CLASS:string = 'GeoPc';
    private geo = null;
    private isPluginInstalled:boolean = false;
    private isPluginUpdated:boolean = false;
    private GCInitialized:boolean = false;
    private waitingForPlugin:boolean = false;
    private geoLocationReject:Function = this.doFailGeoLocation;
    private geoLocationResolve:Function;
    private connectionResolve:Function;
    private connectionReject:Function;
    private postMessageEventListener:any;
    private _installPopupCloseHandler:() => void;
    protected initParams:IGeoPCParams;

    protected init(params:IGeoPCParams) : void {
        super.init(params);
        this.downloadGC = params.downloadGC || this.downloadGC;
    }

    public runWrapper():void{
        let url:string = this.parent.getServiceFactory().http2Service().appendOrigin(this.initParams.geoLib);
        this.parent.getServiceFactory().scriptLoaderService().loadScript(url)
            .then(() => {
                this.initGC();
                this.connectGC().then((response) => {
                    if (response.succeeded) {
                        this.sendAnalytics('GC plugin available on startup');
                    } else {
                        this.sendAnalytics('GC plugin not available on startup');
                    }
                }).catch(() => {
                    this.sendAnalytics('GC plugin exception on startup');
                });
            });
    }

    public isPlugInEnable(bMandatory:boolean):Promise<boolean> {
        if (!this.geo) {
            this.initGC();
        }
        return this.checkIfConnectGC()
            .then((res:boolean) => this.checkIfNeedMsg(res, bMandatory))
            .then((res:boolean) => this.getIsPluginAvailableResponse(res))
            .catch(() => this.getIsPluginAvailableResponse(false))
    }

    private checkIfNeedMsg(res:boolean, bMandatory:boolean): Promise<boolean> {
        if (bMandatory && !res){
            return this.installMsg().then((res)=>Promise.resolve(res.succeeded));
        } else {
            return Promise.resolve(res);
        }
    }

    private checkIfConnectGC(): Promise<boolean> {
        if (this.GCInitialized && this.pluginInstalledAndUpdated()){
            return Promise.resolve(true);
        } else {
            return this.connectGC().then((res)=>Promise.resolve(res.succeeded));
        }
    }

    public getGeoLocation(data:GeoDataFormatter):Promise<void> {
        let error:string = this.setGeoParams(data);

        if(error && error !== '0') {
            this.sendAnalytics('setGeoParams error',{error:error});
        }

        this.requestGeolocation();
        return Promise.resolve();
        // TODO: this flow not cover case if there is no any response from GC library
    }

    private installMsg():Promise<GeoPluginResults> {
        this._installPopupCloseHandler = null;
        return this.showInstallPluginMSG().then((response:boolean) => {
            if (response) {
                this.sendAnalytics('download started');
                return this.startPluginDownload();
            } else {
                this.sendAnalytics('download pop-up closed without download');
                return Promise.resolve(this.getResult(false, 112, 'download pop-up closed without download'));
            }})
            .catch(()=> Promise.resolve(this.getResult(false, 113, 'exception on plugin installation message')));
    }

    private showInstallPluginMSG():Promise<boolean> {
        return new Promise<boolean>(resolve => {
            return this.parent.getServiceFactory().orbitComponentRenderService().showGeoPluginInstallPopup({
                downloadPlugin: (popUpCloseHandler) => {
                    this._installPopupCloseHandler = popUpCloseHandler;
                    resolve(true);
                },
                restartDownloadPlugin:() => {
                    this.downloadGC();
                    this.sendAnalytics('download restarted');
                },
                installPopUpClosed:() => {
                    Event.fire(SYSTEM_EVENTS.GEO_LOCATION_FAILED);
                    if(!this.waitingForPlugin) {
                        resolve(false);
                    }
                    else {//after user clicked download and closed the dialog before finishing installation
                        this.waitingForPlugin = false;//stop GCconnect cyclic attempts
                        if(this._connectGCRejector){
                            this._connectGCRejector();
                            this._connectGCRejector = null;
                        }
                    }
                }
            });
        });
    }

    private startPluginDownload():Promise<GeoPluginResults> {
        this.downloadGC();
        this.waitingForPlugin = true;
        return this.connectGC();
    }

    private initGC():void {
        if (!this.geo) {
            this.geo = (window as any).GeoComply.createClient({
                onConnectSuccess: () => {
                    this.sendAnalytics('GC plugin onConnectSuccess');
                    this.connectionResolve();
                },
                onConnectFailed: (errorCode, errorMessage) => {
                    this.sendAnalytics('GC plugin onConnectFailed');
                    this.connectionReject(errorCode, errorMessage);
                },
                onGeolocationAvailable: (data) => {
                    this.geoLocationResolve(data);
                },
                onGeolocationFailed: (errorCode, errorMessage) => {
                    this.geoLocationReject(errorCode, errorMessage);
                },
                onLog: null
            });
        }
    }

    private _connectGCRejector: any = null;

    private connectGC():Promise<GeoPluginResults> {
        return new Promise<GeoPluginResults>((resolve, reject) => {
            this._connectGCRejector = reject;
            this.startConnectGC().then(response => {
                resolve(response)
            });
        })
    }

    private startConnectGC():Promise<GeoPluginResults> {
        return this.doConnect()
            .then((response:GeoPluginResults) => {
                if (response.succeeded) {
                    this.sendAnalytics('GC plugin connection succeeded');
                    this.doGeoLocationPluginAvailable();
                } else {
                    this.sendAnalytics('GC plugin connection failed',{errorCode:response.errorCode, errorMessage:response.errorMessage});
                    this.doGeoLocationPluginNotAvailable(response.errorCode, response.errorMessage);
                    if (this.waitingForPlugin) {
                        return this.reconnectCycle().then(() => {
                            return this.startConnectGC();
                        });
                    }
                }
                return Promise.resolve(response);
            })
            .catch((errorMessage) => {
                this.sendAnalytics('GC plugin exception on connection');
                return Promise.resolve(this.getResult(false, 111, errorMessage));
            });
    }

    private getResult(succeeded:boolean, errorCode:number=-1, errorMessage:string=''):GeoPluginResults {
        return new GeoPluginResults(succeeded, errorCode, errorMessage);
    }

    private getIsPluginAvailableResponse(succeeded:boolean):Promise<boolean>  {
        return succeeded ? Promise.resolve(true) : Promise.reject(false);
    }

    private reconnectCycle():Promise<void> {
        return new Promise((resolve) => {
            DOM.setTimeout(resolve, GEO_LOCATION.ANTI_LOOP_DELAY);
        });
     }

    private doConnect():Promise<GeoPluginResults> {
        return new Promise((resolve) => {
            this.connectionResolve = () => {
                resolve(this.getResult(true));
            };
            this.connectionReject = (errorCode, errorMessage) => {
                resolve(this.getResult(false, errorCode, errorMessage));
            };// reject;
            this.sendAnalytics('GC plugin connect',{GCinstallerID:this.initParams.GCinstallerID, GCenvironment:this.initParams.GCenvironment});
            this.geo.connect(this.initParams.GCinstallerID, this.initParams.GCenvironment);
        });
    }

    private disconnectGC():void {
        if (this.geo) {
            this.geo.disconnect();
            this.geo = null;
        }
    }

    private setGeoParams(data:GeoDataFormatter):string {
        this.geo.setLicense(data.license);
        this.geo.customFields.set('session_key', data.sessionKey);
        this.geo.setUserPhoneNumber = data.phone;
        this.geo.setGeolocationReason(data.reason);
        this.geo.setUserId(data.userId);
        return this.geo.getLastErrorCode();
    }

    private requestGeolocation():void {
        this.doGeoLocation().then((res:string) => this.doPassGeoLocation(res), (errorCode:string) => this.doFailGeoLocation(errorCode));
    }

    private doGeoLocation():Promise<string> {
        return new Promise((resolve, reject) => {
            this.geoLocationResolve = resolve;
            this.geoLocationReject = reject;
            this.geo.requestGeolocation();
        });
    }

    private downloadGC():void {
        // TODO: once GC plugin will work with https - change to normal file download flow
        let url:any = this.getGCPluginDownloadPage();
        DOM.setTimeout(()=> window.location = url as Location,5000); //Safari MAC needs delay
    }

    private doGeoLocationPluginAvailable():void {
        this.isPluginInstalled = true;
        this.GCInitialized = true;
        this.isPluginUpdated = true;
        this.sendAnalytics('plugin is available');
        if(this._installPopupCloseHandler) {
            this._installPopupCloseHandler();
            this._installPopupCloseHandler = null;
        }
        this.waitingForPlugin = false;
    }

    private doGeoLocationPluginNotAvailable(errorCode, errorMessage):void {
        this.isPluginInstalled = errorCode === GEO_LOCATION.PLUGIN_NOT_UPDATED_ERROR;
        this.isPluginUpdated = false;
        this.GCInitialized = true;
    }

    private doPassGeoLocation(data:string):void {
        this.parent.onGeoLocationResOk(data);
    }

    private doFailGeoLocation(errorCode:string, errorMessage:string=''):void {
        this.parent.onGeoLocationResFailed(parseInt(errorCode), errorMessage);
    }

    private getGCPluginDownloadPage():string {
        let url:string = this.initParams.installPath;
        let params:string = this.initParams.installParams.replace(GEO_LOCATION.GC_KEY, this.initParams.GCinstallerID)
            .replace(GEO_LOCATION.GC_PLATFORM, this.initParams.platform.toLowerCase())
            .replace(GEO_LOCATION.GC_VERSION, this.initParams.downloadVersion);
        url += params;
        return url;
    }

    private pluginInstalledAndUpdated():boolean {
        return this.isPluginInstalled && this.isPluginUpdated;
    }

    public cancelGCConnect():void{
        if(this._connectGCRejector){
            this._connectGCRejector();
            this._connectGCRejector = null;
        }
    }

}
